/**
 * Plain-data description of an item used by list-based components (select,
 * command-palette, context-menu, etc.). The `value` is the stable identifier,
 * `label` is the human-readable text, and `description` is an optional
 * secondary line.
 */
export interface FxListItemData {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

/**
 * A single highlighted substring range. `start` is inclusive, `end` is exclusive.
 */
export interface FxListMatch {
  start: number;
  end: number;
}

/**
 * Result shape returned by a list search engine.
 */
export interface FxListSearchResult<T extends FxListItemData = FxListItemData> {
  item: T;
  matches?: FxListMatch[];
}

/**
 * Search engine contract. Implementations receive the normalized query and the
 * full item list and return the visible items with optional highlight ranges.
 * May be async to support custom remote search.
 */
export type FxListSearchEngine<T extends FxListItemData = FxListItemData> = (
  query: string,
  items: T[]
) => FxListSearchResult<T>[] | Promise<FxListSearchResult<T>[]>;

/**
 * A label split into highlighted and non-highlighted segments. Components render
 * this as they see fit (e.g. `<mark>` elements).
 */
export interface FxListHighlightSegment {
  text: string;
  highlight: boolean;
}

/**
 * Registry that keeps an up-to-date, DOM-ordered data array for list-like
 * components. Child items register/unregister with the root so the root always
 * has the current set to feed into a search engine or render.
 */
export class FxListRegistry<T extends FxListItemData = FxListItemData> {
  private root?: HTMLElement;

  private selector?: string;

  private readonly itemMap = new Map<HTMLElement, T>();

  private readonly valueMap = new Map<string, HTMLElement>();

  constructor(root?: HTMLElement, selector?: string) {
    this.root = root;
    this.selector = selector;
  }

  /**
   * Register an item element with its data. If the same element is
   * re-registered the data is updated.
   */
  register(el: HTMLElement, item: T): void {
    this.itemMap.set(el, item);
    this.valueMap.set(item.value, el);
  }

  /**
   * Remove an item from the registry. The value mapping is only removed when it
   * points to the same element, so re-registering under a different value does
   * not orphan a still-attached sibling.
   */
  unregister(el: HTMLElement): void {
    const item = this.itemMap.get(el);
    if (item) {
      this.itemMap.delete(el);
      const current = this.valueMap.get(item.value);
      if (current === el) {
        this.valueMap.delete(item.value);
      }
    }
  }

  /**
   * Refresh the DOM order and update the stored data for the current nodes. Use
   * this when an item's label or disabled state may have changed.
   */
  sync(getData: (el: HTMLElement) => T): T[] {
    const elements = this.getElements();
    elements.forEach((el) => {
      const data = getData(el);
      this.register(el, data);
    });
    return this.getItems();
  }

  /**
   * Return the registered elements in DOM order. Falls back to registration
   * order when no root/selector is configured.
   */
  getElements(): HTMLElement[] {
    if (this.root && this.selector) {
      return Array.from(this.root.querySelectorAll(this.selector)) as HTMLElement[];
    }
    return Array.from(this.itemMap.keys());
  }

  /**
   * Return the current items in DOM order.
   */
  getItems(): T[] {
    return this.getElements()
      .map((el) => this.itemMap.get(el))
      .filter((item): item is T => item !== undefined);
  }

  /**
   * Return the data for a given value.
   */
  getItem(value: string): T | undefined {
    const el = this.valueMap.get(value);
    return el ? this.itemMap.get(el) : undefined;
  }

  /**
   * Return the element for a given value.
   */
  getElement(value: string): HTMLElement | undefined {
    return this.valueMap.get(value);
  }
}

/**
 * Default list search engine: case-insensitive substring matching against the
 * item label and value. Returns highlight ranges for the label match, if any.
 */
export function defaultListSearch<T extends FxListItemData>(
  query: string,
  items: T[]
): FxListSearchResult<T>[] {
  const normalized = query.toLowerCase().trim();
  if (normalized.length === 0) {
    return items.map((item) => ({ item }));
  }

  return items
    .map((item): FxListSearchResult<T> | null => {
      const labelLower = item.label.toLowerCase();
      const valueLower = item.value.toLowerCase();
      const labelIndex = labelLower.indexOf(normalized);
      const valueMatch = valueLower.indexOf(normalized) !== -1;

      if (labelIndex === -1 && !valueMatch) {
        return null;
      }

      const matches: FxListMatch[] | undefined =
        labelIndex !== -1
          ? [{ start: labelIndex, end: labelIndex + normalized.length }]
          : undefined;

      return { item, matches } as FxListSearchResult<T>;
    })
    .filter((result): result is FxListSearchResult<T> => result !== null);
}

/**
 * Split a label into highlight/non-highlight segments from the provided match
 * ranges. Components render `<mark>` segments for `highlight: true`.
 */
export function highlightLabel(
  label: string,
  matches?: FxListMatch[]
): FxListHighlightSegment[] {
  if (!matches || matches.length === 0) {
    return [{ text: label, highlight: false }];
  }

  const sorted = [...matches].sort((a, b) => a.start - b.start);
  const segments: FxListHighlightSegment[] = [];
  let index = 0;

  for (const match of sorted) {
    if (match.start > index) {
      segments.push({ text: label.slice(index, match.start), highlight: false });
    }
    if (match.end > match.start) {
      segments.push({ text: label.slice(match.start, match.end), highlight: true });
    }
    index = Math.max(index, match.end);
  }

  if (index < label.length) {
    segments.push({ text: label.slice(index), highlight: false });
  }

  return segments;
}
